import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { FileScan, Loader2, Play, Plus } from "lucide-react";

const SAMPLE_TEXT = `IN THE MATTER of the Deeds Registries Act. Certificate of Title in favour of Chirandu Family Trust for Stand 4127 Harare Township measuring 823 square metres, registered on 14th of March 1978. Bounded on the north by Stand 4126, on the east by Second Avenue road reserve, on the south by Stand 4128, on the west by Stand 4113.`;

export const DigitisationCentre = () => {
  const qc = useQueryClient();
  const [text, setText] = useState(SAMPLE_TEXT);
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const [newBatch, setNewBatch] = useState({ title: "", source: "Deeds Registry" });

  const batches = useQuery({ queryKey: ["digitisation"], queryFn: async () => (await api.get("/digitisation/batches")).data });

  const create = useMutation({
    mutationFn: async () => (await api.post("/digitisation/batches", newBatch)).data,
    onSuccess: () => { toast.success("Batch created"); qc.invalidateQueries({ queryKey: ["digitisation"] }); setNewBatch({ title: "", source: "Deeds Registry" }); },
    onError: (e) => toast.error(formatApiError(e)),
  });

  const classify = async () => {
    setBusy(true); setResult(null);
    try {
      const fd = new FormData();
      fd.append("text", text);
      const { data } = await api.post("/digitisation/classify", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setResult(data);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-6" data-testid="page-digitisation">
      <header>
        <div className="font-mono-cad text-[10px] uppercase tracking-[0.28em] text-[#7C93A5]">National Legacy Digitisation Centre</div>
        <h1 className="font-display text-3xl font-bold text-[#F6F8FA] mt-1 flex items-center gap-2"><FileScan className="text-[#C99A2E]"/> Legacy Records Programme</h1>
        <div className="text-[13px] text-[#B3C2CC] mt-1">Bulk scanning · OCR · AI classification · duplicate detection · digital preservation.</div>
      </header>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="cad-panel p-5 lg:col-span-2">
          <div className="font-mono-cad text-[10px] uppercase tracking-[0.24em] text-[#C99A2E]">AI Classification demo</div>
          <div className="text-[12px] text-[#B3C2CC] mt-1">Paste OCR text from a scanned record. The Cadastre Intelligence Engine will classify it, extract entities and provide a summary.</div>
          <textarea value={text} onChange={(e) => setText(e.target.value)} rows={6} data-testid="dig-text"
                    className="mt-3 w-full bg-[#071B2B] border cad-hairline rounded-sm px-2 py-2 text-[12.5px] text-[#F6F8FA]" />
          <button onClick={classify} disabled={busy || !text.trim()} data-testid="dig-classify"
                  className="cad-btn-primary mt-3 px-4 py-2 rounded-sm text-[13px] inline-flex items-center gap-1.5">
            {busy ? <Loader2 size={14} className="animate-spin"/> : <Play size={14}/>} Classify
          </button>

          {result && (
            <div className="mt-4 border cad-hairline rounded-sm p-3 bg-[#071B2B]" data-testid="dig-result">
              <div className="flex items-center justify-between">
                <span className="cad-chip cad-chip-gold uppercase">{result.classification.category}</span>
                <span className="font-mono-cad text-[11px] text-[#7C93A5]">confidence {Math.round((result.classification.confidence || 0) * 100)}%</span>
              </div>
              <div className="text-[13px] text-[#F6F8FA] mt-2 leading-relaxed">{result.classification.summary}</div>
              {result.classification.entities?.length > 0 && (
                <div className="mt-2 flex flex-wrap gap-1">
                  {result.classification.entities.map((e, i) => <span key={i} className="cad-chip cad-chip-blue text-[10px]">{e}</span>)}
                </div>
              )}
            </div>
          )}
        </div>

        <div className="cad-panel p-5">
          <div className="font-mono-cad text-[10px] uppercase tracking-[0.24em] text-[#C99A2E]">New scanning batch</div>
          <div className="space-y-2 mt-3">
            <input value={newBatch.title} onChange={(e) => setNewBatch({ ...newBatch, title: e.target.value })}
                   placeholder="Batch title" data-testid="dig-new-title"
                   className="w-full bg-[#071B2B] border cad-hairline rounded-sm px-2 py-2 text-[13px] text-[#F6F8FA]" />
            <input value={newBatch.source} onChange={(e) => setNewBatch({ ...newBatch, source: e.target.value })}
                   placeholder="Source archive" data-testid="dig-new-source"
                   className="w-full bg-[#071B2B] border cad-hairline rounded-sm px-2 py-2 text-[13px] text-[#F6F8FA]" />
            <button onClick={() => create.mutate()} disabled={!newBatch.title || create.isPending} data-testid="dig-new-submit"
                    className="cad-btn-outline w-full py-2 rounded-sm text-[13px] inline-flex items-center justify-center gap-1.5">
              <Plus size={13}/> Create batch
            </button>
          </div>
        </div>
      </div>

      <div className="cad-panel overflow-hidden">
        <div className="px-5 py-3 border-b cad-hairline">
          <div className="font-mono-cad text-[10px] uppercase tracking-[0.24em] text-[#7C93A5]">Batches</div>
        </div>
        <table className="w-full text-[13px]">
          <thead>
            <tr className="text-left border-b cad-hairline">
              {["Title","Source","Pages","Classified","Duplicates","Confidence","Status","Created"].map((h) => (
                <th key={h} className="px-3 py-2 font-mono-cad text-[10px] uppercase tracking-[0.18em] text-[#7C93A5]">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody className="cad-tbody">
            {(batches.data || []).map((b) => (
              <tr key={b.id} data-testid={`dig-batch-${b.id}`}>
                <td className="px-3 py-2 text-[#F6F8FA]">{b.title}</td>
                <td className="px-3 py-2 text-[#DCE3E8]">{b.source}</td>
                <td className="px-3 py-2 font-mono-cad">{b.pages_total}</td>
                <td className="px-3 py-2 font-mono-cad">
                  <div className="flex items-center gap-2">
                    <div className="w-20 h-1.5 bg-[#18232D] rounded-full overflow-hidden">
                      <div className="h-full bg-[#C99A2E]" style={{ width: `${(b.pages_classified / (b.pages_total || 1)) * 100}%` }} />
                    </div>
                    <span>{b.pages_classified}</span>
                  </div>
                </td>
                <td className="px-3 py-2 font-mono-cad">{b.duplicates_found}</td>
                <td className="px-3 py-2 font-mono-cad">{Math.round((b.confidence_avg || 0) * 100)}%</td>
                <td className="px-3 py-2">
                  <span className={`cad-chip ${b.status === "approved" ? "cad-chip-green" : b.status === "quality_review" ? "cad-chip-gold" : "cad-chip-blue"}`}>{b.status.replace(/_/g, " ")}</span>
                </td>
                <td className="px-3 py-2 font-mono-cad text-[#B3C2CC]">{(b.created_at || "").slice(0, 10)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};
